import { monsterArt, gearArt, DEX_STYLE } from '../dex-art.js';
import { itemGlyph } from '../item-glyphs.js';
/**
 * ── **도감** (DESIGN 14.57.3~4) ──
 *
 * 사용자: *"아이템 도감"*.
 *
 * ── 이 화면의 값어치는 **빈 칸**에 있다 ────────────────────────
 *
 * 설계서가 못박은 문장이 이 파일의 규칙 전부다:
 *
 * > **채운 칸보다 빈 칸이 다음에 갈 곳을 가리킨다.**
 *
 * 그래서 **못 본 것을 숨기지 않는다.** 흔한 도감 화면은 미획득을 회색 실루엣으로
 * 흐려놓는데, 그러면 *"뭐가 없는지"* 를 세려면 눈으로 살펴야 한다. 여기서는
 * **빈 칸에 어디서 나오는지를 적는다** — 일반 상점 등급, 유리샘 전용 등급,
 * 드롭 전용 등급을 가른다. ⚠ 그 판정도 **지어내지 않는다** — 지역 상점 함수가 답한다.
 *
 * ── **가졌는가가 아니라 본 적이 있는가** ───────────────────────
 *
 * 가진 것으로 세면 **버리면 지워지고**, 그러면 도감이 **창고를 못 비우게 만든다**
 * (갇힘 검사 항목 14.56). 그래서 서버가 *"본 적이 있는가"* 를 계정에 쌓고, 이 화면은
 * 그것을 그리기만 한다. **화면은 아무것도 세지 않는다.**
 *
 * ── 몬스터도 같은 원칙이다 ────────────────────────────────────
 *
 * `MONSTERS` 를 클라에 두 번째로 베끼지 않는다. 서버가 전체 목록과 첫 처치 세계를
 * 같이 보내므로 빈 칸도 진짜 표이고, 새 몬스터를 추가하면 저절로 따라온다.
 * 떠돌이는 `MONSTERS` 가 아니라 Player 계열이므로 이 표에 섞이지 않는다.
 */
import { S, esc } from '../../core/state.js';
import {
  GLASS_SHOP_MAX_GRADE, RIFT_SHOP_MAX_GRADE, GRADES, JUNK_KO, POTIONS, ACCS, CARRY_SIGILS,
  SHOP_MAX_GRADE, SKILL_IMPRINTS, WINGS, TEMPLE_RING, gradeCol as _gc,
} from '../../world/map.js';
import { kindName, SLOT_KINDS, SLOT_NAME } from '../../world/gearnames.js';
import { shopSaleSlotNames, sourceLabel } from '../../world/gearsource.js';
import { GAMBLE_POOL } from '../../world/gamble.js';
import { cell, fold, gearGlyph, muted, num, sec, split , junkName } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { levelGrowthRules, levelGrowthSummary } from '../level-growth.js';

/**
 * 도감이 다루는 장비 부위 — **표에서 뽑는다.** 손으로 적었더니 부위가 일곱이 된 뒤에도
 * 둘만 세고 있었다. 이름표(`kinds`)가 없는 부위(가방)는 등급이 없으므로 뺀다 —
 * 가방은 아래 `ACCS` 절이 따로 센다.
 */
const SLOTS = Object.keys(SLOT_KINDS).map((id) => ({ id, ko: SLOT_NAME[id] }));

/**
 * 등급 칸 하나.
 *
 * ⚠ **이름을 `kindName` 으로 낸다.** 도감이 자기 이름표를 따로 들면 가방과
 * 다르게 부르게 되고, 그건 *"내가 본 그것"* 인지 알 수 없게 만든다.
 * `id` 는 이름을 고르는 해시라 **고정값**을 준다 — 도감은 특정 자루가 아니라
 * **그 등급을 봤는가**를 말하므로, 매번 다른 이름이 나오면 안 된다.
 */
function gearCell(slot, g, seen) {
  const key = `${slot.id}:${g.id}`;
  const on = seen.has(key);
  const name = kindName(slot.id, g.id, false, 'dex');
  const from = sourceLabel(slot.id, g.id);
  return `<article class="dexone" style="--dex-accent:${gradeColOf(g.id)}" title="${esc(name)}">
    <div class="dexmeta"><span>${slot.ko}</span><b style="color:${gradeColOf(g.id)}">${g.name}</b></div>
    <div class="dexart gear${slot.id==='weapon'?' variants':''}">${(slot.id==='weapon'?[0,1,2]:[undefined]).map(style=>`<img src="${gearArt(slot.id,g.id,style)}" alt="${g.name} ${slot.ko} 실제 착용 견본" width="88" height="88" loading="lazy">`).join('')}</div>
    <span class="dexnm${on ? '' : ' empty'}">${g.name} ${slot.ko}</span>
    <span class="dexstatus${on ? '' : ' empty'}">${on ? '✓ 본 적이 있다' : '아직 못 봤다'}</span>
    <span class="qt">${from} · ${g.name}</span></article>`;
}

/** 등급 색은 지도 미러가 갖는다. 없으면(옛 판) 조용히 기본색 */
function gradeColOf(g) {
  try { return _gc(g); } catch { return '#8a7f6b'; }
}

/** 이름표만 있는 칸(잡템·소모품·악세) */
function flatCell(kind, id, ko, seen, tint, glyph, from = '') {
  const on = seen.has(id);
  return '<div class="dexone">'
    + cell({
      // 위와 같은 이유로 `on` 이 아니라 `badge` 다 — 「착용」이 아니라 「본 적 있다」
      key: `dex:${kind}:${id}`, tint, glyph: itemGlyph(kind,id) || glyph, badge: on ? '✓' : '',
      title: on ? `${ko} — 본 적이 있다` : `${ko} — 아직 못 봤다`,
    })
    + `<span class="dexnm${on ? '' : ' empty'}">${ko}</span>`
    + `<span class="dexstatus${on ? '' : ' empty'}">${on ? '✓ 본 적이 있다' : '아직 못 봤다'}</span>`
    + (from ? `<span class="qt">${from}</span>` : '')
    + '</div>';
}

export function render() {
  const d = S.dex;
  if (d?.error) return sec('도감', muted('도감 기록을 읽지 못했다.')
    + '<div class="row"><button onclick="act.dexRefresh()">다시 불러오기</button></div>');
  if (!d) {
    return split(
      sec('도감', muted('<span class="ldspin" aria-hidden="true"></span>도감을 불러오는 중… 오면 저절로 채워진다.')),
      sec('도감 기록', muted(
        '처치한 몬스터와 획득한 아이템을 기록한다.<br>'
        + '버려도 지워지지 않고, <b>세계가 바뀌어도 남는다.</b>')));
  }

  const gear = new Set(d.gear || []);
  const junk = new Set(d.junk || []);
  const pot = new Set(d.pot || []);
  const acc = new Set(d.acc || []);
  const memoryList = d.memory?.list || [];
  const memorySeen = d.memory?.seen || {};

  // UNI-Q36 — 서약의 반지 한 칸. 「받았는가」는 계정·몸의 공개 표식(`you.templeRing.revealed`)이 답한다
  const tr = S.you?.templeRing;
  const ringSeen = !!tr?.revealed;

  // ── 위: 얼마나 남았나. **채운 수가 아니라 남은 수를 앞에 둔다** ──
  const total = SLOTS.length * GRADES.length + Object.keys(JUNK_KO).length + POTIONS.length + ACCS.length + memoryList.length + 1;
  const got = gear.size + junk.size + pot.size + acc.size + Object.keys(memorySeen).length + (ringSeen ? 1 : 0);
  const left = Math.max(0, total - got);

  const focus = sec('도감',
    '<nav class="dexnav" aria-label="도감 분류"><a href="#dex-monsters">몬스터</a><a href="#dex-equipment">장비</a><a href="#dex-materials">재료·유물</a><a href="#dex-consumables">소모품</a><a href="#dex-memories">칭호</a><a href="#dex-history">세계 기록</a></nav>' +
    `<div class="dexsummary"><span>아직 못 본 것 <b>${num(left)}</b></span><span>본 것 <b>${num(got)} / ${num(total)}</b></span></div>`
    + muted(left > 0
      // ★ 설계서의 그 문장을 사람에게도 그대로 말한다
      // ⚠ *"누르면 나온다"* 라고 썼다가 고쳤다 — 칸을 눌러도 아무 일도 안 한다
      //    (`showTip` 이 모르는 열쇠라 조용히 닫는다). **화면이 못 지킬 약속을 하면
      //    사람은 자기가 잘못 눌렀다고 생각한다.** 값은 이미 칸 아래에 적혀 있다.
      ? '아직 못 본 물건도 출처를 따라 찾아갈 수 있다.'
      : '<b>전부 봤다.</b> 이 계정에 남는다 — 세계가 바뀌어도 지워지지 않는다.'));

  let list = '';
  // 전투 행동도 기능 도감에서 한 번은 설명한다. 수집 수에는 넣지 않아 도감 완성률을
  // 바꾸지 않고, 새 설정을 놓친 사람에게 실제 조작 위치를 남긴다.
  list += sec('전투 행동',
    '<div class="kv"><span>자동 반격</span><b>기본 꺼짐</b></div>'
    + muted('설정(O) 또는 가방 → 물품 → 자동 동작에서 켤 수 있다. 나를 공격한 몬스터·경비병·플레이어를 최대 9초·12타일까지 추격한다.'));
  list += sec('레벨과 생존', levelGrowthSummary(S.you?.levelGrowth)
    + fold('레벨 성장과 전투 규칙', muted(levelGrowthRules())
      + muted('플레이어와 원주민에게 같은 성장 규칙을 적용한다. 레벨이 높아도 장비·스탯·치명타에 따라 전투 결과는 달라진다.')));

  // ── 아래: 표 ──
  const worlds = d.worlds || [];
  const contributors = d.contributors || [];
  let history = sec('오래 산 세계', worlds.length
    ? worlds.map((r, i) => `<div class="kv">
        <span><i class="qt">${i + 1}</i> 제 ${num(r.world)} 세계</span>
        <b>${num(Math.floor(r.seconds / 86400))}일 ${num(Math.floor((r.seconds % 86400) / 3600))}시간
          <i class="qt">· 연장 ${num(Math.round(r.extended / 3600))}시간</i></b>
      </div>`).join('')
    : muted(`<b>아직 끝난 세계가 없다.</b><br>${S.world ? `제 ${num(S.world.n)} 세계가 첫 기록을 쓰고 있다.` : '첫 세계가 기록을 쓰고 있다.'}`));
  history += sec('생명의 돌 기여 비석', contributors.length
    ? contributors.map((r, i) => `<div class="kv">
        <span><i class="qt">${i + 1}</i> ${esc(r.name)} <i class="qt">· 제 ${num(r.world)} 세계</i></span>
        <b>${num(r.stones)}개 · ${num(Math.round(r.seconds / 60))}분</b>
      </div>`).join('')
    : muted('<b>아직 아무도 없다.</b><br>첫 생명의 돌을 바친 이름이 여기에 남는다.'));
  const faced = d.doom || [];
  history += sec('드래곤에 맞선 기록', faced.length
    ? faced.map(r => `<div class="kv"><span>${esc(r.name)} <i class="qt">제 ${num(r.world)} 세계 · ${num(r.segment + 1)}구간</i></span><b>유효 피해 ${num(Math.round(r.damage))}<i class="qt"> · 지연 ${num(Math.round(r.earned))}초${r.fell ? ' · 종말 드래곤에게 쓰러짐' : ''}</i></b></div>`).join('')
    : muted('종말 드래곤에 맞서 번 시간이 세계가 끝난 뒤에도 이 계정에 남는다.'));
  list += '<div id="dex-equipment"></div>';
  // 부위를 안 말하면 "장갑도 상점?" 오해가 그대로 남는다 — 파는 부위는 표에서 뽑는다.
  // 2026-10-21 — 전에는 같은 문단이 부위마다(여섯 번) 섰다. 장비 표 맨 위에 한 번만 둔다.
  list += muted(`완제품 상점 판매는 <b>${shopSaleSlotNames(SLOT_NAME).join('·')}</b>뿐 — `
    + `<b>${GRADES[SHOP_MAX_GRADE].name}</b>까지, 비늘바람 마을은 <b>${GRADES[RIFT_SHOP_MAX_GRADE].name}</b>까지, 유리샘 마을은 <b>${GRADES[GLASS_SHOP_MAX_GRADE].name}</b>까지 판다. `
    + `다른 부위도 <b>${GAMBLE_POOL.at(-1).name}</b>까지는 운장수 상자깡·사냥에서 얻는다. `
    + '그보다 높은 등급은 사냥·승급으로 얻는다.');
  for (const slot of SLOTS) {
    const mine = GRADES.filter((g) => gear.has(`${slot.id}:${g.id}`)).length;
    list += `<div class="sec lazy-sec"><h3>${slot.ko} <em>${mine}/${GRADES.length}</em></h3>`
      + '<div class="dexgrid">'
      + GRADES.map((g) => gearCell(slot, g, gear)).join('')
      + '</div>'
      + '</div>';
  }

  const carryKeys = new Set(CARRY_SIGILS.map((d) => d.key));
  const relicKeys = new Set(WINGS.map((d) => d.key));
  const imprintKeys = new Set(SKILL_IMPRINTS.map((d) => d.key));
  const junkKeys = Object.keys(JUNK_KO).filter((k) => !carryKeys.has(k) && !relicKeys.has(k) && !imprintKeys.has(k));
  const junkGot = junkKeys.filter((k) => junk.has(k)).length;
  list += `<div id="dex-materials" class="sec lazy-sec"><h3>재료 <em>${junkGot}/${junkKeys.length}</em></h3>`
    + '<div class="dexgrid">'
    + junkKeys.map((k) => flatCell('j', k, junkName(k), junk, '#8c7853', '◆')).join('')
    + '</div></div>';

  const carryGot = CARRY_SIGILS.filter((d) => junk.has(d.key)).length;
  list += `<div class="sec lazy-sec"><h3>무게 각인 <em>${carryGot}/${CARRY_SIGILS.length}</em></h3>`
    // 이름과 출처가 모두 긴 다섯 칸이다. 일반 도감의 60px 칸을 그대로 쓰면
    // 모바일에서 다섯 열로 잘게 찢어져 한 글자씩 읽게 된다 — 각인만 넓은 칸을 쓴다.
    + '<div class="dexgrid carrydex">'
    + CARRY_SIGILS.map((d) => flatCell('j', d.key, d.name, junk, '#63dfc4', '⊕', d.from)).join('')
    + '</div>'
    + muted('각인별 획득처를 확인해 달라. 봇짐장수에게서 다음 단계 각인을 확정 구매할 수도 있다.')
    + '</div>';

  const imprintGot = SKILL_IMPRINTS.filter((d) => junk.has(d.key)).length;
  list += `<div class="sec lazy-sec"><h3>스킬 각인 <em>${imprintGot}/${SKILL_IMPRINTS.length}</em></h3>`
    + '<div class="dexgrid carrydex">'
    + SKILL_IMPRINTS.map((d) => flatCell('j', d.key, d.name, junk, d.color, '✧', d.from)).join('')
    + '</div>' + muted('유리심연 2~4층 상징 보스의 완성석. 획득한 뒤 가방에서 새길 때마다 한 단계씩 끝없이 성장한다.') + '</div>';

  const relicGot = [...relicKeys].filter((k) => junk.has(k)).length;
  list += `<div class="sec lazy-sec"><h3>유물 <em>${relicGot}/${relicKeys.size}</em></h3>`
    + '<div class="dexgrid carrydex">'
    + WINGS.map((d) => flatCell('j', d.key, d.name, junk, d.color, '✦', d.desc + ((S.dex?.relicSources?.[d.key]?.length) ? ' · 획득처: ' + S.dex.relicSources[d.key].join(' · ') : ''))).join('')
    + '</div>' + muted('각 날개의 획득처에서 아주 낮은 확률로 드롭한다. 날개 없이도 성향 선3을 갖추면 사망 시 장비를 지킬 수 있다. 성향과 보호 상태는 가방에서 확인한다.') + '</div>';

  list += `<div id="dex-consumables" class="sec lazy-sec"><h3>소모품 <em>${pot.size}/${POTIONS.length}</em></h3>`
    + '<div class="dexgrid">'
    + POTIONS.map((p) => flatCell('c', p.id, p.name, pot, '#c9736c', '◈')).join('')
    + '</div></div>';

  list += `<div class="sec lazy-sec"><h3>악세사리 <em>${acc.size}/${ACCS.length}</em></h3>`
    + '<div class="dexgrid">'
    + ACCS.map((a) => flatCell('a', a.id, a.name, acc, '#7fa8c9', '▣')).join('')
    + '</div></div>';

  /**
   * UNI-Q36 — **서약의 반지는 이 도감의 명시적 예외다.** 머리말의 원칙(못 본 것을 숨기지 않는다)은
   * *어디서 나오는가* 를 숨기지 않는다는 뜻이고, 여기서도 출처(신전 사제·생명의 돌·은화)는 적는다.
   * 숨기는 것은 **수치 하나**다 — 사용자 확정(2026-09-08): 교환 전엔 성능 비공개, 받은 이만 본다.
   * 값은 서버가 `you.templeRing.base` 로 **받은 이에게만** 보낸다. 화면은 산식을 갖지 않는다.
   */
  const ringCost = tr?.cost;
  list += `<div class="sec"><h3>${TEMPLE_RING.name} <em>${ringSeen ? 1 : 0}/1</em></h3>`
    + '<div class="dexgrid carrydex">'
    + '<div class="dexone">' + cell({
      key: 'dex:ring:' + TEMPLE_RING.id, tint: gradeColOf(TEMPLE_RING.grade), glyph: gearGlyph('ring'), badge: ringSeen ? '✓' : '',
      title: ringSeen ? `${TEMPLE_RING.name} — 사제에게서 받았다` : `${TEMPLE_RING.name} — 아직 받지 않았다`,
    })
    + `<span class="dexnm${ringSeen ? '' : ' empty'}">${GRADES[TEMPLE_RING.grade].name} ${TEMPLE_RING.name}</span>`
    + `<span class="dexstatus${ringSeen ? '' : ' empty'}">${ringSeen ? '✓ 사제에게서 받았다' : '아직 받지 않았다'}</span>`
    + `<span class="qt">신전 사제 · ${ringCost ? `생명의 돌 ${num(ringCost.stones)}개 + 은화 ${num(ringCost.silver)}` : '생명의 돌과 은화'}</span>`
    + `<span class="qt">${ringSeen && Number.isFinite(tr.base) ? `모든 능력치 +${num(tr.base)} · 신화 +0 기준, 강화·궁극 승급으로 오른다` : '능력치 비공개 — 받은 이만 안다'}</span>`
    + '</div></div>'
    + muted('<b>사제에게서 반지를 받으면</b> 능력치가 공개된다.')
    + '</div>';

  /**
   * 보스의 기억은 서버가 보스 표에서 만든다. 클라이언트에 보스 이름을 한 벌 더 적으면
   * 새 보스가 추가된 날 도감만 빈 칸을 잃는다. 해금한 기억은 짧은 칭호로 선택할 수 있다.
   */
  list += '<div id="dex-memories"></div>';
  if (memoryList.length) {
    const selected = d.memory?.selected || '';
    const n = Object.keys(memorySeen).length;
    list += `<div class="sec lazy-sec"><h3>보스의 기억 <em>${n}/${memoryList.length}</em></h3>`
      + muted('자연 보스에게서 드물게 되찾는 계정 영구 기록이다. 전투력은 없고, 하나를 칭호로 내걸 수 있다.')
      + '<div class="dexgrid carrydex">'
      + memoryList.map((m) => {
        const world = memorySeen[m.id];
        const on = selected === m.id;
        return '<div class="dexone">'
          + cell({
            key: 'dex:memory:' + m.id, tint: world ? '#9277d6' : '#2e2a22', glyph: '◈', badge: world ? '✓' : '',
            title: world ? `${m.name} — 제 ${world} 세계에서 되찾았다` : `${m.name} — ${m.from}`,
          })
          + `<span class="dexnm${world ? '' : ' empty'}">${esc(m.name)}</span>`
          + `<span class="qt">${world ? `제 ${num(world)} 세계 · 〈${esc(m.title)}〉` : esc(m.from)}</span>`
          + (world ? `<button class="wide${on ? ' gold' : ''}" onclick="act.bossTitle('${on ? '' : m.id}')">${on ? '칭호 내리기' : '칭호 사용'}</button>` : '')
          + '</div>';
      }).join('')
      + '</div></div>';
  }

  /**
   * R122(2026-09-29) — 후원자 칭호. 서버가 후원한 계정에만 `memory.supporter` 를 보낸다(없으면 절도 없다).
   * 보스의 기억 칭호와 같은 자리(`act.bossTitle`)라 하나만 걸린다. 전투력은 없다.
   */
  if (d.memory?.supporter) {
    const sup = d.memory.supporter;
    const on = (d.memory?.selected || '') === sup.id;
    list += '<div class="sec lazy-sec"><h3>후원자 칭호</h3>'
      + fold('후원 표식 · 걸 때만 이름 위에 보인다(전투력 없음)', '후원해 준 계정의 표식이다. 전투력·장비·은화는 없고, <b>걸 때만</b> 이름 위에 〈…〉로 보인다. 보스의 기억 칭호와 같은 자리라 하나만 걸린다. 칭호는 계정에 남아 세계가 바뀌어도 다시 걸 수 있다.')
      + '<div class="dexgrid carrydex"><div class="dexone">'
      + cell({ key: 'dex:supporter', tint: '#c2566f', glyph: '♥', badge: '✓', title: `〈${sup.title}〉 — 후원 감사` })
      + `<span class="dexnm">〈${esc(sup.title)}〉</span>`
      + `<button class="wide${on ? ' gold' : ''}" onclick="act.bossTitle('${on ? '' : esc(sup.id)}')">${on ? '칭호 내리기' : '칭호 사용'}</button>`
      + '</div></div></div>';
  }

  /**
   * 몬스터 — **표가 오면 절이 생긴다.** 없는 동안 자리를 만들어 두지 않는다.
   * `firstWorld` 는 *"몇 번째 세계에서 처음 잡았나"* 다 — 오래된 것일수록 값지다.
   */
  if (d.mon && d.mon.list) {
    const seenMon = d.mon.seen || {};
    const cardList = d.mon.list.map(m => {
      const w=seenMon[m.id], rank=m.boss?'보스':m.elite?'네임드':'일반';
      return `<article class="dexone ${m.boss?'boss':m.elite?'elite':''}" data-mon-id="${esc(m.id)}">
        <div class="dexmeta"><b>${rank}</b><span>Lv.${levelLabel(m.level || 1)}</span></div>
        <div class="dexart"><img src="${monsterArt(m.sprite)}" alt="${esc(m.ko)} 실제 모습" width="112" height="112" loading="lazy"></div>
        ${(m.relics || []).map(d => `<span class="dexstatus">${esc(d.name)} · 처치당 ${num(d.rate * 100)}%</span>`).join('')}
        <span class="dexnm${w?'':' empty'}">${esc(m.ko)}</span>
        <span class="dexstatus${w?'':' empty'}">${w?`✓ 제 ${num(w)} 세계 첫 처치`:'아직 못 잡았다'}</span>
      </article>`;
    });
    // UNI-Q110 ⑦ — 몬스터 카드는 한 절이 3만px(PC 실측)라 12장씩 격자 덩어리(.lazy-sec)로 나눠 접힌 곳 아래는 자리만 남긴다.
    const cards = [];
    for (let i = 0; i < cardList.length; i += 12) cards.push(`<div class="dexgrid lazy-sec">${cardList.slice(i, i + 12).join('')}</div>`);
    list = `<section id="dex-monsters" class="sec"><h3>몬스터 <em>${Object.keys(seenMon).length}/${d.mon.list.length}</em></h3>${cards.join('')}</section>` + list;
  }

  list += `<div id="dex-history">${fold('이 계정이 함께 남긴 세계 기록', history)}</div>`;
  return split(DEX_STYLE + focus, list).replace('class="split"', 'class="split dex-catalog"');
}
